import { useState } from 'react';
import intl from 'react-intl-universal';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import {
  selectActiveRequest,
  selectActiveRequestQuestions,
  selectActiveRequestSliceStatus,
} from 'state/ActiveRequest/activeRequestSlice';
import { selectUserType, selectOrganizationId } from 'state/User/userSlice';
import { requestPermissionsSelectors } from 'state/normalized/requestPermissions/requestPermissionsSlice';
import { Button, Tabs } from '@getsynapse/design-system';
import { SLICE_STATUS, USER_TYPES } from 'utils/constants';
import { getRequestRoute } from 'utils/dynamicRoutes';
import Loader from 'Molecules/Loader/Loader';
import BasicDetails from 'Pages/RequestPage/BasicDetailsPage';
import RequestDetails from 'Pages/RequestPage/RequestDetailsPage';
import AdditionalDetails from 'Pages/RequestPage/AdditionalDetailsPage';
import LDOnlyPage from 'Pages/RequestPage/LDOnlyPage';
import ArchivedRequestBanner from 'Pages/RequestPage/components/ArchiveRequest/ArchivedRequestBanner';

const ViewRequestModal = () => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const request = useSelector(selectActiveRequest);
  const requestQuestions = useSelector(selectActiveRequestQuestions);
  const requestSliceStatus = useSelector(selectActiveRequestSliceStatus);
  const userType = useSelector(selectUserType);
  const organizationId = useSelector(selectOrganizationId);
  const isRequestArchived = request?.isArchived;
  const archiveDate = request?.archiveDate;
  const requestId = request?.id;
  const requestPermissions = useSelector((state: RootState) =>
    requestId
      ? requestPermissionsSelectors.selectById(state, requestId)
      : undefined
  );
  const canView = requestPermissions?.canView === true;

  const handleOpenInNewTab = () => {
    if (!requestId) return;
    const route = getRequestRoute(requestId, organizationId, {
      isJsonSchema: false,
    });
    window.open(`${window.location.origin}${route}`, '_blank');
  };

  const isLoadingRequest =
    requestSliceStatus === SLICE_STATUS.LOADING || !request?.requester;

  if (isLoadingRequest) {
    return (
      <div
        className='p-8 flex justify-center items-center min-h-40'
        data-cy='view-linked-request-modal__loader'
      >
        <Loader />
      </div>
    );
  }

  return (
    <div className='p-1' data-cy='view-linked-request-modal__body'>
      {isRequestArchived && (
        <ArchivedRequestBanner
          archiveDate={archiveDate!}
          canUnarchive={false}
          requestId={requestId!}
        />
      )}
      {canView && (
        <div className='flex justify-end mb-4'>
          <Button
            variant='secondary'
            data-cy='view-linked-request-modal__open-request'
            onClick={handleOpenInNewTab}
          >
            {intl.get('NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.OPEN_REQUEST')}
          </Button>
        </div>
      )}
      <Tabs
        index={currentTabIndex}
        onChange={(index: number) => setCurrentTabIndex(index)}
        tabListProps={{
          className: 'sticky top-0 z-5 bg-neutral-white',
        }}
        data={[
          {
            label: intl.get(
              'NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.BASIC_DETAILS'
            ),
            content: (
              <BasicDetails
                requestData={request}
                requestQuestionsData={requestQuestions}
                disableFields
                hideViewProject={true}
              />
            ),
          },
          {
            label: intl.get(
              'NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.REQUEST_DETAILS'
            ),
            content: (
              <RequestDetails
                requestData={request}
                requestQuestionsData={requestQuestions}
                disableFields
              />
            ),
          },
          {
            label: intl.get(
              'NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.ADDITIONAL_DETAILS'
            ),
            content: (
              <AdditionalDetails
                requestData={request}
                requestQuestionsData={requestQuestions}
                disableFields
              />
            ),
          },
          ...(userType === USER_TYPES.L_D
            ? [
                {
                  label: intl.get(
                    'NEW_PROJECT_PAGE.VIEW_PROJECT_MODAL.LD_ONLY'
                  ),
                  content: (
                    <LDOnlyPage
                      requestData={request}
                      requestQuestionsData={requestQuestions}
                      disableFields
                    />
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default ViewRequestModal;
